import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { 
  FiSave, FiCreditCard, FiCheck, FiX, FiAlertTriangle, 
  FiExternalLink, FiLock, FiKey, FiEye, FiEyeOff, FiRefreshCw,
  FiUpload, FiPackage, FiClipboard, FiCheckCircle
} from 'react-icons/fi';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function MyPOSSettingsPage() {
  const [settings, setSettings] = useState({
    mypos_sid: '',
    mypos_wallet_number: '',
    mypos_private_key: '',
    mypos_key_index: '3',
    mypos_test_mode: true,
    mypos_production_domain: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingDomain, setSavingDomain] = useState(false);
  const [testing, setTesting] = useState(false);
  const [configStatus, setConfigStatus] = useState(null);
  const [showPrivateKey, setShowPrivateKey] = useState(false);
  
  // Configuration Package import
  const [configPackage, setConfigPackage] = useState('');
  const [importing, setImporting] = useState(false);
  const [showConfigPackageInput, setShowConfigPackageInput] = useState(false);

  useEffect(() => {
    loadSettings();
    checkConfigStatus();
  }, []);

  const loadSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSettings(prev => ({
          ...prev,
          mypos_sid: data.mypos_sid || '',
          mypos_wallet_number: data.mypos_wallet_number || '',
          mypos_private_key: data.mypos_private_key || '',
          mypos_key_index: data.mypos_key_index || '3',
          mypos_test_mode: data.mypos_test_mode !== false,
          mypos_production_domain: data.mypos_production_domain || ''
        }));
      }
    } catch (error) {
      console.error('Error loading settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkConfigStatus = async () => {
    try {
      const response = await fetch(`${API_URL}/api/payments/mypos/config`);
      if (response.ok) {
        const data = await response.json();
        setConfigStatus(data);
      }
    } catch (error) {
      console.error('Error checking config:', error);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/settings/mypos`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(settings)
      });

      if (response.ok) {
        toast.success('Configuration MyPOS enregistrée');
        checkConfigStatus();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de l\'enregistrement');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    } finally {
      setSaving(false);
    }
  };

  const handleImportPackage = async () => {
    if (!configPackage.trim()) {
      toast.error('Veuillez coller le Configuration Package');
      return;
    }
    
    setImporting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/settings/mypos/import-package`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ config_package: configPackage.trim() })
      });

      const data = await response.json();
      
      if (response.ok) {
        toast.success('Configuration MyPOS importée avec succès !');
        // Update local state with extracted values
        setSettings(prev => ({
          ...prev,
          mypos_sid: data.extracted.sid,
          mypos_wallet_number: data.extracted.wallet_number,
          mypos_key_index: data.extracted.key_index,
          mypos_private_key: '*** Clé privée importée ***'
        }));
        setConfigPackage('');
        setShowConfigPackageInput(false);
        checkConfigStatus();
        loadSettings();
      } else {
        toast.error(data.detail || 'Erreur lors de l\'import');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    } finally {
      setImporting(false);
    }
  };

  const handleTestConnection = async () => {
    setTesting(true);
    try {
      const response = await fetch(`${API_URL}/api/payments/mypos/config`);
      if (response.ok) {
        const data = await response.json();
        setConfigStatus(data);
        if (data.configured) {
          toast.success('Configuration MyPOS valide');
        } else {
          toast.warning('Configuration incomplète');
        }
      }
    } catch (error) {
      toast.error('Erreur lors du test');
    } finally {
      setTesting(false);
    }
  };

  const handleSaveProductionDomain = async () => {
    setSavingDomain(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/settings/mypos/production-domain`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ production_domain: settings.mypos_production_domain })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success('Domaine de production configuré !');
        setSettings(prev => ({ ...prev, mypos_production_domain: data.production_domain }));
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de l\'enregistrement');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    } finally {
      setSavingDomain(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="mypos-settings-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Configuration MyPOS</h1>
          <p className="text-slate-500 mt-1">Configurez votre intégration de paiement MyPOS IPC</p>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" onClick={handleTestConnection} disabled={testing}>
            <FiRefreshCw className={`mr-2 h-4 w-4 ${testing ? 'animate-spin' : ''}`} />
            Tester
          </Button>
          <Button onClick={handleSave} disabled={saving} className="bg-amber-600 hover:bg-amber-700">
            <FiSave className="mr-2 h-4 w-4" />
            {saving ? 'Enregistrement...' : 'Enregistrer'}
          </Button>
        </div>
      </div>

      {/* Status Card */}
      <Card className={`border-2 ${configStatus?.configured ? 'border-green-200 bg-green-50' : 'border-amber-200 bg-amber-50'}`}>
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${
              configStatus?.configured ? 'bg-green-100' : 'bg-amber-100'
            }`}>
              {configStatus?.configured ? (
                <FiCheck className="text-2xl text-green-600" />
              ) : (
                <FiAlertTriangle className="text-2xl text-amber-600" />
              )}
            </div>
            <div className="flex-1">
              <h3 className={`font-semibold ${configStatus?.configured ? 'text-green-900' : 'text-amber-900'}`}>
                {configStatus?.configured ? 'MyPOS configuré et opérationnel' : 'Configuration requise'}
              </h3>
              <p className={`text-sm mt-1 ${configStatus?.configured ? 'text-green-700' : 'text-amber-700'}`}>
                {configStatus?.configured 
                  ? `Mode ${configStatus.test_mode ? 'Test (Sandbox)' : 'Production'} activé`
                  : 'Importez votre Configuration Package ou saisissez vos identifiants manuellement'
                }
              </p>
              {configStatus && (
                <div className="flex flex-wrap gap-2 mt-3">
                  <Badge variant={configStatus.sid_configured ? 'default' : 'secondary'}>
                    {configStatus.sid_configured ? <FiCheck className="mr-1" /> : <FiX className="mr-1" />}
                    Store ID (SID)
                  </Badge>
                  <Badge variant={configStatus.wallet_configured ? 'default' : 'secondary'}>
                    {configStatus.wallet_configured ? <FiCheck className="mr-1" /> : <FiX className="mr-1" />}
                    Wallet
                  </Badge>
                  <Badge variant={configStatus.private_key_configured ? 'default' : 'secondary'}>
                    {configStatus.private_key_configured ? <FiCheck className="mr-1" /> : <FiX className="mr-1" />}
                    Clé Privée RSA
                  </Badge>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Quick Import - Configuration Package */}
      <Card className="border-2 border-blue-200 bg-gradient-to-br from-blue-50 to-indigo-50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-blue-900">
            <FiPackage className="text-blue-600" />
            Import rapide avec Configuration Package
          </CardTitle>
          <CardDescription className="text-blue-700">
            Méthode recommandée : Importez automatiquement toutes vos credentials MyPOS en un clic
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {!showConfigPackageInput ? (
            <div className="flex items-center gap-4">
              <div className="flex-1">
                <p className="text-sm text-blue-800">
                  <strong>Où trouver votre Configuration Package ?</strong><br/>
                  Dashboard MyPOS → Online Stores → Votre boutique → <strong>Configuration Package</strong> (chaîne base64)
                </p>
              </div>
              <Button 
                onClick={() => setShowConfigPackageInput(true)}
                className="bg-blue-600 hover:bg-blue-700"
              >
                <FiUpload className="mr-2 h-4 w-4" />
                Importer un package
              </Button>
            </div>
          ) : (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="space-y-4"
            >
              <div className="bg-white rounded-lg p-4 border border-blue-200">
                <Label className="flex items-center gap-2 text-blue-900 mb-2">
                  <FiClipboard className="h-4 w-4" />
                  Collez votre Configuration Package ici
                </Label>
                <Textarea
                  value={configPackage}
                  onChange={(e) => setConfigPackage(e.target.value)}
                  placeholder="eyJzaWQiOiIxMjM0NTY3ODkwIiwiY24iOi..."
                  rows={4}
                  className="font-mono text-xs bg-slate-50"
                  data-testid="config-package-input"
                />
                <p className="text-xs text-slate-500 mt-2">
                  Le package est une longue chaîne de caractères commençant généralement par "eyJ"
                </p>
              </div>
              
              <div className="flex gap-3 justify-end">
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setShowConfigPackageInput(false);
                    setConfigPackage('');
                  }}
                >
                  Annuler
                </Button>
                <Button 
                  onClick={handleImportPackage}
                  disabled={importing || !configPackage.trim()}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {importing ? (
                    <>
                      <FiRefreshCw className="mr-2 h-4 w-4 animate-spin" />
                      Import en cours...
                    </>
                  ) : (
                    <>
                      <FiCheckCircle className="mr-2 h-4 w-4" />
                      Importer et configurer
                    </>
                  )}
                </Button>
              </div>
            </motion.div>
          )}
        </CardContent>
      </Card>

      {/* Manual Configuration Form */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FiCreditCard className="text-amber-600" />
            Configuration manuelle
          </CardTitle>
          <CardDescription>
            Ou saisissez vos identifiants manuellement si vous n'avez pas de Configuration Package
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Help Box */}
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
            <h4 className="font-medium text-slate-900 mb-2 flex items-center gap-2">
              <FiExternalLink className="h-4 w-4" />
              Où trouver ces informations ?
            </h4>
            <ol className="text-sm text-slate-700 space-y-1 list-decimal list-inside">
              <li>Connectez-vous à votre <a href="https://developers.mypos.com" target="_blank" rel="noopener noreferrer" className="underline hover:text-blue-600 text-blue-500">espace développeur MyPOS</a></li>
              <li>Accédez à <strong>Online Stores</strong> → votre boutique</li>
              <li>Copiez le <strong>Store ID (SID)</strong> et le <strong>Client Number (Wallet)</strong></li>
              <li>Dans l'onglet <strong>Certificates</strong>, téléchargez votre clé privée RSA</li>
            </ol>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <Label htmlFor="mypos_sid" className="flex items-center gap-2">
                <FiKey className="h-4 w-4" />
                Store ID (SID) *
              </Label>
              <Input
                id="mypos_sid"
                value={settings.mypos_sid}
                onChange={(e) => setSettings({ ...settings, mypos_sid: e.target.value })}
                placeholder="1234567"
                className="mt-2"
                data-testid="mypos-sid-input"
              />
              <p className="text-xs text-slate-500 mt-1">Identifiant numérique de votre boutique</p>
            </div>

            <div>
              <Label htmlFor="mypos_wallet_number" className="flex items-center gap-2">
                <FiCreditCard className="h-4 w-4" />
                Wallet Number (Client Number) *
              </Label>
              <Input
                id="mypos_wallet_number"
                value={settings.mypos_wallet_number}
                onChange={(e) => setSettings({ ...settings, mypos_wallet_number: e.target.value })}
                placeholder="40882269398"
                className="mt-2"
                data-testid="mypos-wallet-input"
              />
              <p className="text-xs text-slate-500 mt-1">Numéro de votre compte MyPOS</p>
            </div>
          </div>

          <div>
            <Label htmlFor="mypos_key_index" className="flex items-center gap-2">
              Key Index
            </Label>
            <Input
              id="mypos_key_index"
              value={settings.mypos_key_index}
              onChange={(e) => setSettings({ ...settings, mypos_key_index: e.target.value })}
              placeholder="1"
              className="mt-2 w-32"
              data-testid="mypos-keyindex-input"
            />
            <p className="text-xs text-slate-500 mt-1">Index de la clé (généralement 1)</p>
          </div>

          <div>
            <Label htmlFor="mypos_private_key" className="flex items-center gap-2">
              <FiLock className="h-4 w-4" />
              Clé Privée RSA *
            </Label>
            <div className="relative mt-2">
              <Textarea
                id="mypos_private_key"
                value={settings.mypos_private_key}
                onChange={(e) => setSettings({ ...settings, mypos_private_key: e.target.value })}
                placeholder="-----BEGIN RSA PRIVATE KEY-----
MIIEowIBAAKCAQEA...
-----END RSA PRIVATE KEY-----"
                rows={6}
                className={`font-mono text-xs ${!showPrivateKey ? 'text-transparent select-none' : ''}`}
                style={!showPrivateKey ? { textShadow: '0 0 5px rgba(0,0,0,0.5)' } : {}}
                data-testid="mypos-privatekey-input"
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="absolute top-2 right-2"
                onClick={() => setShowPrivateKey(!showPrivateKey)}
              >
                {showPrivateKey ? <FiEyeOff className="h-4 w-4" /> : <FiEye className="h-4 w-4" />}
              </Button>
            </div>
            <p className="text-xs text-slate-500 mt-1">
              Copiez l'intégralité de la clé, incluant les lignes BEGIN et END
            </p>
          </div>

          {/* Test Mode Toggle */}
          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
            <div>
              <Label className="text-base font-medium">Mode Test (Sandbox)</Label>
              <p className="text-sm text-slate-500">
                Activez ce mode pour tester les paiements sans transactions réelles
              </p>
            </div>
            <Switch
              checked={settings.mypos_test_mode}
              onCheckedChange={(v) => setSettings({ ...settings, mypos_test_mode: v })}
              data-testid="mypos-testmode-switch"
            />
          </div>

          <div className={`p-4 rounded-lg ${settings.mypos_test_mode ? 'bg-purple-50 border border-purple-200' : 'bg-green-50 border border-green-200'}`}>
            <p className={`text-sm font-medium ${settings.mypos_test_mode ? 'text-purple-800' : 'text-green-800'}`}>
              {settings.mypos_test_mode 
                ? '🧪 Mode Test actif - Les paiements seront simulés via checkout-test.mypos.eu'
                : '✅ Mode Production actif - Les paiements seront traités réellement via checkout.mypos.eu'
              }
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Production Domain Configuration */}
      <Card className="border-2 border-orange-200 bg-gradient-to-br from-orange-50 to-amber-50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-orange-900">
            <FiExternalLink className="text-orange-600" />
            Domaine de production (URLs de callback)
          </CardTitle>
          <CardDescription className="text-orange-700">
            <strong>Important :</strong> Configurez le domaine autorisé dans votre compte MyPOS pour les URLs de retour
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-white rounded-lg p-4 border border-orange-200">
            <Label htmlFor="mypos_production_domain" className="flex items-center gap-2 text-orange-900 mb-2">
              <FiExternalLink className="h-4 w-4" />
              Domaine de production
            </Label>
            <div className="flex gap-3">
              <Input
                id="mypos_production_domain"
                value={settings.mypos_production_domain}
                onChange={(e) => setSettings({ ...settings, mypos_production_domain: e.target.value })}
                placeholder="https://app.justasmile-events.com"
                className="flex-1"
                data-testid="mypos-production-domain-input"
              />
              <Button 
                onClick={handleSaveProductionDomain}
                disabled={savingDomain}
                className="bg-orange-600 hover:bg-orange-700"
              >
                {savingDomain ? (
                  <FiRefreshCw className="h-4 w-4 animate-spin" />
                ) : (
                  <FiSave className="h-4 w-4" />
                )}
              </Button>
            </div>
            <p className="text-xs text-orange-700 mt-2">
              Les URLs de callback (succès, annulation, notification) utiliseront ce domaine au lieu du domaine actuel.
              <br/>
              <strong>Exemple :</strong> Si vous configurez <code className="bg-orange-100 px-1 rounded">https://app.justasmile-events.com</code>, 
              MyPOS redirigera vers <code className="bg-orange-100 px-1 rounded">https://app.justasmile-events.com/api/payments/mypos/success</code>
            </p>
          </div>
          
          {settings.mypos_production_domain && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
              <p className="text-sm text-green-800">
                <FiCheck className="inline mr-2" />
                <strong>URLs de callback configurées :</strong>
              </p>
              <ul className="text-xs text-green-700 mt-2 space-y-1 font-mono">
                <li>✓ {settings.mypos_production_domain}/api/payments/mypos/success</li>
                <li>✓ {settings.mypos_production_domain}/api/payments/mypos/cancel</li>
                <li>✓ {settings.mypos_production_domain}/api/payments/mypos/notify</li>
              </ul>
            </div>
          )}
          
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
            <p className="text-sm text-amber-800">
              <FiAlertTriangle className="inline mr-2" />
              <strong>N'oubliez pas</strong> d'ajouter ce domaine dans votre dashboard MyPOS sous 
              <strong> Online Stores → Settings → Allowed URLs</strong>
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Documentation Link */}
      <Card className="bg-slate-50">
        <CardContent className="p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-slate-200 flex items-center justify-center">
              <FiExternalLink className="text-xl text-slate-600" />
            </div>
            <div className="flex-1">
              <h4 className="font-semibold text-slate-900">Documentation MyPOS</h4>
              <p className="text-sm text-slate-600">
                Consultez la documentation officielle pour plus d'informations sur l'intégration IPC Checkout
              </p>
            </div>
            <Button variant="outline" asChild>
              <a 
                href="https://developers.mypos.com/apis/checkout-api/checkout-getting-started" 
                target="_blank" 
                rel="noopener noreferrer"
              >
                <FiExternalLink className="mr-2 h-4 w-4" />
                Voir la documentation
              </a>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
